import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicePageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:before {
        content: "✓";
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

const ImageSection = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
  }
`;

const GradeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const GradeCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  text-align: center;

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
    font-family: "Phudu", sans-serif;
  }

  .condition {
    background: ${(props) =>
      props.grade === "A+"
        ? "#28a745"
        : props.grade === "A"
        ? "#17a2b8"
        : props.grade === "B"
        ? "#ffc107"
        : "#dc3545"};
    color: white;
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: bold;
    margin-bottom: 15px;
    display: inline-block;
  }

  p {
    color: var(--text-light);
    margin-bottom: 20px;
    font-size: 0.9rem;
  }

  .price {
    font-size: 1.25rem;
    color: var(--secondary-color);
    font-weight: bold;
  }
`;

const CTASection = styled.div`
  background: var(--background-dark);
  color: white;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    margin-bottom: 30px;
    opacity: 0.9;
  }

  .btn {
    background: var(--secondary-color);
    color: white;
    padding: 15px 30px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(245, 115, 58, 0.3);
    }
  }
`;

const UsedContainers = () => {
  return (
    <ServicePageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Used Containers</PageTitle>
          <PageSubtitle>
            Affordable, dependable used containers carefully inspected and
            certified for quality. Perfect for budget-conscious storage
            solutions.
          </PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <ContentSection>
            <h2>Quality Used Containers</h2>
            <p>
              Our used containers offer exceptional value without compromising
              on quality. Each container is carefully inspected and graded to
              ensure it meets our high standards for durability and reliability.
            </p>
            <p>
              Perfect for cost-effective storage solutions, these containers
              provide secure, weatherproof storage at a fraction of the cost of
              new units.
            </p>
            <ul>
              <li>Wind and watertight certified</li>
              <li>Thoroughly inspected for structural integrity</li>
              <li>Multiple condition grades available</li>
              <li>Cost-effective storage solution</li>
              <li>Suitable for personal and business use</li>
              <li>Available in various sizes</li>
            </ul>
          </ContentSection>

          <ImageSection>
            <img src="/assets/2025/08/Image3.jpg" alt="Used Container" />
          </ImageSection>
        </ContentGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Container Grades & Pricing
        </h2>

        <GradeGrid>
          <GradeCard grade="A+">
            <h3>Grade A+ (Premium)</h3>
            <div className="condition">Excellent</div>
            <p>
              Like-new condition with minimal wear. Perfect paint, no dents,
              clean interior. Ideal for high-visibility applications.
            </p>
            <div className="price">Starting at $3,800</div>
          </GradeCard>

          <GradeCard grade="A">
            <h3>Grade A (Superior)</h3>
            <div className="condition">Very Good</div>
            <p>
              Minor surface wear with good paint. Small dents possible but
              structurally sound. Great for most storage needs.
            </p>
            <div className="price">Starting at $3,200</div>
          </GradeCard>

          <GradeCard grade="B">
            <h3>Grade B (Standard)</h3>
            <div className="condition">Good</div>
            <p>
              Visible wear and possible rust spots. Some dents and paint wear
              but fully functional and watertight.
            </p>
            <div className="price">Starting at $2,600</div>
          </GradeCard>

          <GradeCard grade="C">
            <h3>Grade C (Economy)</h3>
            <div className="condition">Fair</div>
            <p>
              Significant wear with rust and dents. Still structurally sound and
              secure. Budget-friendly option.
            </p>
            <div className="price">Starting at $2,000</div>
          </GradeCard>
        </GradeGrid>

        <CTASection>
          <h2>Find Your Perfect Container</h2>
          <p>
            Browse our inventory of quality used containers or contact us for
            personalized recommendations based on your specific needs.
          </p>
          <Link to="/contact" className="btn">
            View Inventory
          </Link>
        </CTASection>
      </Container>
    </ServicePageWrapper>
  );
};

export default UsedContainers;
